(() => {
  const SELECTORS = {
    container: '#ProductGridContainer',
    grid: '#product-grid',
    wrapper: '[data-load-more-wrapper]',
    button: '[data-load-more]',
    sentinel: '[data-load-more-sentinel]',
    spinner: '[data-load-more-spinner]',
    meta: '[data-load-more-meta]',
    viewed: '[data-load-more-viewed]',
    total: '[data-load-more-total]',
    of: '[data-load-more-of]',
    label: '[data-load-more-label]',

    // Only count real result cards
    item: 'li[data-product-card]',

    // PLP header hooks (Liquid emits these)
    headerCount: '[data-plp-result-count]',
    headerCountSr: '[data-plp-result-count-sr]',
  };

  // Existing tuning
  const OBSERVER_ROOT_MARGIN = '300px 0px';
  const MIN_TIME_BETWEEN_LOADS_MS = 900;
  const SCROLL_DELTA_REQUIRED_PX = 120;
  const SPINNER_DELAY_MS = 200;
  const APPEND_CHUNK_SIZE = 12;

  // Only apply the "end on a full row" batching tweak on search results.
  // Collections (PLPs) keep the original behavior.
  const TEMPLATE = document.body?.dataset?.template || '';
  const TEMPLATE_ROOT = TEMPLATE.split('.')[0];
  const ENABLE_FULL_ROW_BATCHING =
    TEMPLATE_ROOT === 'search' ||
    document.body?.classList?.contains('template-search');

  // Batch display tuning
  // Base target is the server's page size (#product-grid[data-page-size], which the
  // collection section re-balances so a handful of leftovers never sits behind its own
  // LOAD MORE — e.g. 42 products render as one page of 42). Falls back to 40 where the
  // grid doesn't declare one (search). On search results it's adjusted to a multiple of
  // the grid column count so visible results end on a full row above the button.
  const BASE_BATCH_SIZE = 40;

  let baseBatchSize = BASE_BATCH_SIZE;
  let batchSize = baseBatchSize;
  let shownLimit = batchSize;

  // Track grid column count (used for full-row alignment)
  let gridColumns = 0;

  // Track URL state for batching reset on filter/sort change
  let batchKey = null;

  let isLoading = false;
  let spinnerTimer = null;
  let intersectionObserver = null;
  let mutationObserver = null;

  let lastLoadStartedAt = 0;
  let lastTriggerScrollY = 0;
  let lastKnownScrollY = window.scrollY;

  let cooldownUntil = 0;

  // A load that outlives the grid it was started for (a filter, sort or Back
  // render replaced it meanwhile) must not write into the new grid: its
  // pagination block would point LOAD MORE at the old state's next page.
  let loadGeneration = 0;
  let loadAbortController = null;
  // A stalled request would otherwise hold isLoading, and the button, forever.
  const LOAD_TIMEOUT_MS = 12000;

  // Total-count state (read from container's data-plp-total attribute)
  let totalResults = null;

  // Small-final-page fold: when a load leaves at most this many products behind
  // (#product-grid[data-load-more-min-remaining], the section's setting), the final
  // page is fetched straight away and appended, so LOAD MORE never stands in front of
  // a handful of leftovers. Each next-page href is folded at most once so a failing
  // fetch can't loop.
  const foldedHrefs = new Set();

  const readMinRemaining = (container) => {
    const grid = container?.querySelector?.(SELECTORS.grid);
    const n = parseInt(grid?.dataset?.loadMoreMinRemaining || '', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  // Next-page prefetch — stores the response HTML so Load More clicks render instantly.
  // (Section responses are `Cache-Control: private, no-store`, so we must keep the body in memory.)
  const prefetchedPages = new Map();
  const prefetchInFlight = new Set();

  window.addEventListener(
    'scroll',
    () => {
      lastKnownScrollY = window.scrollY;
    },
    { passive: true },
  );

  const getContainer = () => document.querySelector(SELECTORS.container);

  const getState = () => {
    const container = getContainer();
    if (!container) return {};
    const grid = container.querySelector(SELECTORS.grid);
    const wrapper = container.querySelector(SELECTORS.wrapper);

    return {
      container,
      grid,
      wrapper,
      button: wrapper?.querySelector(SELECTORS.button) || null,
      sentinel: wrapper?.querySelector(SELECTORS.sentinel) || null,
      spinner: wrapper?.querySelector(SELECTORS.spinner) || null,
      meta: wrapper?.querySelector(SELECTORS.meta) || null,
    };
  };

  const buildSectionUrl = (href, sectionId) => {
    const url = new URL(href, window.location.origin);
    if (!sectionId) return null;
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  };

  const parseSectionHTML = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    const newContainer = temp.querySelector(SELECTORS.container) || temp;
    return {
      newGrid: newContainer.querySelector(SELECTORS.grid),
      newWrapper: newContainer.querySelector(SELECTORS.wrapper),
    };
  };

  // Background-prefetch the next-page URL and hold the HTML in memory for instant reveal.
  const prefetchNextPage = (container) => {
    const grid = container?.querySelector(SELECTORS.grid);
    const wrapper = container?.querySelector(SELECTORS.wrapper);
    const btn = wrapper?.querySelector(SELECTORS.button);
    const href = btn?.getAttribute('href');
    if (!href || !href.trim() || !grid?.dataset?.id) return;

    const fetchUrl = buildSectionUrl(href, grid.dataset.id);
    if (!fetchUrl) return;
    if (prefetchedPages.has(fetchUrl) || prefetchInFlight.has(fetchUrl)) return;
    prefetchInFlight.add(fetchUrl);

    const run = async () => {
      try {
        const res = await fetch(fetchUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (res.ok) {
          const html = await res.text();
          prefetchedPages.set(fetchUrl, html);
        }
      } catch (e) {
        // swallow — next user click will fetch normally
      } finally {
        prefetchInFlight.delete(fetchUrl);
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 1500 });
    } else {
      setTimeout(run, 250);
    }
  };

  // ---------- Batch helpers (40 at a time) ----------

  const countTopLevelSpaceSeparatedTokens = (str) => {
    if (!str) return 0;
    const s = String(str).trim();
    if (!s || s === 'none') return 0;

    // If the browser returns a repeat() function, prefer parsing the repeat count.
    // Example: repeat(4, minmax(0px, 1fr))
    const repeatMatch = s.match(/^repeat\((\d+),/i);
    if (repeatMatch) {
      const n = parseInt(repeatMatch[1], 10);
      return Number.isFinite(n) ? n : 0;
    }

    // Otherwise, count tokens separated by spaces at depth 0 (ignore spaces inside parentheses)
    let depth = 0;
    let count = 0;
    let inToken = false;

    for (let i = 0; i < s.length; i += 1) {
      const ch = s[i];
      if (ch === '(') depth += 1;
      else if (ch === ')' && depth > 0) depth -= 1;

      const isSpace = depth === 0 && /\s/.test(ch);
      if (isSpace) {
        if (inToken) {
          count += 1;
          inToken = false;
        }
      } else {
        inToken = true;
      }
    }

    if (inToken) count += 1;
    return count;
  };

  const getColumnsFromGridClasses = (grid) => {
    if (!grid) return 0;
    const classes = Array.from(grid.classList || []);

    // Dawn-style classes:
    // - grid--2-col-tablet-down
    // - grid--4-col-desktop
    const desktop = classes
      .map((c) => c.match(/^grid--(\d+)-col-desktop$/))
      .find(Boolean);

    const tabletDown = classes
      .map((c) => c.match(/^grid--(\d+)-col-tablet-down$/))
      .find(Boolean);

    // Dawn uses ~990px as the desktop breakpoint
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const m = isDesktop ? desktop || tabletDown : tabletDown || desktop;
    if (!m) return 0;

    const n = parseInt(m[1], 10);
    return Number.isFinite(n) ? n : 0;
  };

  const detectGridColumns = (grid) => {
    if (!grid) return 0;

    try {
      const style = window.getComputedStyle(grid);
      const cols = style?.gridTemplateColumns;
      const n = countTopLevelSpaceSeparatedTokens(cols);
      if (n && n > 0) return n;
    } catch (e) {
      // ignore
    }

    // Fallback: parse Dawn grid classes
    return getColumnsFromGridClasses(grid);
  };

  // Read the server page size off the grid; the batch must match it or the JS would
  // hide the tail of a re-balanced page (e.g. cards 41-42) behind a reveal-only button.
  const syncBaseBatchSize = (container) => {
    const grid = container?.querySelector?.(SELECTORS.grid);
    const n = parseInt(grid?.dataset?.pageSize || '', 10);
    const next = Number.isFinite(n) && n > 0 ? n : BASE_BATCH_SIZE;
    if (next !== baseBatchSize) {
      baseBatchSize = next;
      batchSize = computeBatchSize(gridColumns);
    }
  };

  const computeBatchSize = (columns) => {
    const cols = Number.isFinite(columns) && columns > 0 ? columns : 0;
    if (cols <= 1) return baseBatchSize;

    const remainder = baseBatchSize % cols;
    const adjusted = remainder === 0 ? baseBatchSize : baseBatchSize - remainder;

    // Guard: never return 0
    return adjusted > 0 ? adjusted : cols;
  };

  const normalizeBatching = (container) => {
    if (!ENABLE_FULL_ROW_BATCHING) return;

    const grid = container?.querySelector?.(SELECTORS.grid);
    if (!grid) return;

    const cols = detectGridColumns(grid);
    if (cols && cols !== gridColumns) {
      gridColumns = cols;
      batchSize = computeBatchSize(gridColumns);
    } else if (!gridColumns) {
      gridColumns = cols || 0;
      batchSize = computeBatchSize(gridColumns);
    }

    // Ensure shownLimit stays aligned to a full row
    if (gridColumns > 1 && shownLimit > 0) {
      const rem = shownLimit % gridColumns;
      if (rem !== 0) shownLimit = Math.max(gridColumns, shownLimit - rem);
    }

    // If we have fewer cards loaded than our limit, but there IS more content to load
    // (next page exists), hide the trailing partial row so the button sits under a clean grid.
    const wrapper = container.querySelector(SELECTORS.wrapper);
    const btn = wrapper?.querySelector?.(SELECTORS.button) || null;
    const hasNextPage = hasFetchHref(btn);

    const totalLoaded = countAllGridItems(grid);
    if (hasNextPage && gridColumns > 1 && totalLoaded > 0 && totalLoaded < shownLimit) {
      const remLoaded = totalLoaded % gridColumns;
      if (totalLoaded >= gridColumns && remLoaded !== 0) {
        shownLimit = totalLoaded - remLoaded;
      } else {
        shownLimit = totalLoaded;
      }
    }
  };

  const getGridItems = (grid) =>
    Array.from(grid?.children || []).filter(
      (el) => el.nodeType === 1 && el.matches(SELECTORS.item),
    );

  const countAllGridItems = (grid) => getGridItems(grid).length;

  const countVisibleGridItems = (grid) =>
    getGridItems(grid).filter((el) => !el.hidden).length;

  const applyBatchVisibility = (grid) => {
    if (!grid) return;
    const items = getGridItems(grid);
    items.forEach((el, idx) => {
      el.hidden = idx >= shownLimit;
    });
  };

  const hasFetchHref = (btn) => {
    const href = btn?.getAttribute?.('href');
    return typeof href === 'string' && href.trim() !== '';
  };

  // If Liquid didn't render pagination (paginate.pages === 1) but we still have
// hidden cards (because of batching), create the wrapper so we can inject
// a reveal-only "Load more" control.
const ensureLoadMoreWrapper = (container) => {
  let wrapper = container?.querySelector(SELECTORS.wrapper);
  if (wrapper) return wrapper;

  const w = document.createElement('div');
  w.className = 'plp-pagination';
  w.setAttribute('data-load-more-wrapper', '');

  // Minimal markup needed for your existing JS to work
  w.innerHTML = `
    <div class="plp-load-more__spinner" data-load-more-spinner hidden aria-hidden="true">
      <div class="plp-spinner" aria-hidden="true"></div>
    </div>
    <div class="plp-load-more__meta" data-load-more-meta>
      <span data-load-more-viewed>0</span>
      <span data-load-more-of hidden> of </span>
      <span data-load-more-total>0</span>
    </div>
  `;

  const grid = container.querySelector(SELECTORS.grid);
  if (grid?.parentNode) grid.parentNode.appendChild(w);
  else container.appendChild(w);

  return w;
};

  // Ensures the user can always reveal hidden items even when paginate.next is gone.
  const ensureRevealControls = (container) => {
    const grid = container?.querySelector(SELECTORS.grid);
    if (!grid) return;

    const totalLoaded = countAllGridItems(grid);
    const totalShown = countVisibleGridItems(grid);
    const hasHidden = totalLoaded > totalShown;

    // IMPORTANT: If wrapper is missing (single-page paginate), create it only if needed
    let wrapper = container.querySelector(SELECTORS.wrapper);
    if (!wrapper && hasHidden) {
      wrapper = ensureLoadMoreWrapper(container);
    }
    if (!wrapper) return;

    const existingBtn = wrapper.querySelector(SELECTORS.button);
    const serverHasNext = hasFetchHref(existingBtn);

    // We need a button if:
    // - there are hidden cards to reveal, OR
    // - server has next page to fetch
    const needsButton = hasHidden || serverHasNext;

    // If there is no button and we still need one, inject a reveal-only button.
    if (!existingBtn && needsButton) {
      const btn = document.createElement('a');
      btn.className = 'plp-load-more__button';
      btn.setAttribute('href', ''); // blank href => reveal-only
      btn.setAttribute('data-load-more', '');
      btn.setAttribute('data-load-more-generated', 'true');
      btn.setAttribute('aria-label', 'Load more');
      btn.innerHTML =
        '<span class="plp-load-more__label" data-load-more-label>LOAD MORE</span>';

      const meta = wrapper.querySelector(SELECTORS.meta);
      if (meta) wrapper.insertBefore(btn, meta);
      else wrapper.prepend(btn);
    }

    // If we injected a button earlier but it’s no longer needed, remove it.
    const generatedBtn = wrapper.querySelector(
      `${SELECTORS.button}[data-load-more-generated="true"]`,
    );
    if (generatedBtn && !needsButton) generatedBtn.remove();

    // Sentinel: if a button exists, ensure there is a sentinel so auto-load works.
    const btnNow = wrapper.querySelector(SELECTORS.button);
    const sentinelNow = wrapper.querySelector(SELECTORS.sentinel);
    if (btnNow && !sentinelNow) {
      const s = document.createElement('div');
      s.className = 'plp-load-more__sentinel';
      s.setAttribute('data-load-more-sentinel', '');
      s.setAttribute('data-load-more-generated', 'true');
      s.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(s);
    }

    // If we injected a sentinel earlier but there’s no button anymore, remove it.
    const generatedSentinel = wrapper.querySelector(
      `${SELECTORS.sentinel}[data-load-more-generated="true"]`,
    );
    const btnAfter = wrapper.querySelector(SELECTORS.button);
    if (generatedSentinel && !btnAfter) generatedSentinel.remove();

    // Signal the section CSS whether the PLP is fully loaded (no LOAD MORE button
    // left). When complete, the 4-column image grid hugs the footer on mobile —
    // same as a collection that fits one page. While more can load, keep the
    // bottom padding so the LOAD MORE button isn't glued to the footer.
    document.documentElement.setAttribute(
      'data-plp-loaded',
      needsButton ? 'partial' : 'complete',
    );
  };

  // ---------- Header + meta syncing ----------

  const readTotalFromContainer = (container) => {
    const raw = container?.dataset?.plpTotal;
    if (raw == null || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  };

  const setHeaderTotal = (total) => {
    document.querySelectorAll(SELECTORS.headerCount).forEach((el) => {
      el.textContent = String(total);
      el.setAttribute('data-plp-count-state', 'ready');
    });

    document.querySelectorAll(SELECTORS.headerCountSr).forEach((sr) => {
      sr.textContent = `${total} results`;
    });
  };

  const syncViewedCount = (container) => {
    const grid = container?.querySelector(SELECTORS.grid);
    const wrapper = container?.querySelector(SELECTORS.wrapper);
    if (!grid) return;

    const visible = countVisibleGridItems(grid);
    if (totalResults != null) {
      setHeaderTotal(totalResults);
    }

    if (wrapper) {
      const viewedEl = wrapper.querySelector(SELECTORS.viewed);
      if (viewedEl) viewedEl.textContent = String(visible);

      const totalEl = wrapper.querySelector(SELECTORS.total);
      const ofEl = wrapper.querySelector(SELECTORS.of);
      if (totalEl && totalResults != null) {
        totalEl.textContent = String(totalResults);
        if (ofEl) ofEl.hidden = false;
      }
    }

    // Keep the visible text clean (no brackets), keep remaining info for screen readers only
    if (wrapper && totalResults != null) {
      const btn = wrapper.querySelector(SELECTORS.button);
      const label = btn?.querySelector(SELECTORS.label);
      if (btn && label) {
        const remaining = Math.max(0, totalResults - visible);
        label.textContent = 'LOAD MORE';
        btn.setAttribute(
          'aria-label',
          remaining > 0
            ? `Load more. ${remaining} remaining.`
            : 'Load more.',
        );
      }
    }
  };

  const refreshTotalFromContainer = (container) => {
    const t = readTotalFromContainer(container);
    if (t != null) {
      totalResults = t;
      setHeaderTotal(totalResults);
    }
  };

  // ---------- Spinner + button state ----------

  const setSpinnerVisible = (container, visible) => {
    const spinner = container?.querySelector(SELECTORS.spinner);
    if (!spinner) return;
    spinner.hidden = !visible;
  };

  const setButtonLoading = (container, loading) => {
    const btn = container?.querySelector(SELECTORS.button);
    if (!btn) return;

    if (loading) {
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');
    } else {
      btn.removeAttribute('aria-busy');
      btn.classList.remove('is-loading');
    }
  };

  const showCooldownMessage = (container, ms) => {
    const meta = container?.querySelector(SELECTORS.meta);
    if (!meta) return;

    if (!meta.dataset.originalHtml) meta.dataset.originalHtml = meta.innerHTML;
    meta.textContent = 'Too many requests — try again in a moment.';

    window.setTimeout(() => {
      const c = getContainer();
      const m = c?.querySelector(SELECTORS.meta);
      if (!m) return;
      if (m.dataset.originalHtml) m.innerHTML = m.dataset.originalHtml;
      delete m.dataset.originalHtml;

      // Re-apply batch + counts after cooldown message restores
      const grid = c?.querySelector(SELECTORS.grid);
      applyBatchVisibility(grid);
      ensureRevealControls(c);
      syncViewedCount(c);
      refreshObserver();
    }, ms);
  };

  // ---------- Append helper ----------

  const appendItemsChunked = async (
    targetGrid,
    sourceGrid,
    isCurrent = () => true,
  ) => {
    const items = Array.from(sourceGrid?.children || []).filter(
      (el) => el.nodeType === 1 && el.matches(SELECTORS.item),
    );

    for (let i = 0; i < items.length; i += APPEND_CHUNK_SIZE) {
      if (!isCurrent()) return;
      const frag = document.createDocumentFragment();
      const chunk = items.slice(i, i + APPEND_CHUNK_SIZE);

      for (const item of chunk) {
        item.querySelectorAll('img').forEach((img) => {
          if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
          if (!img.hasAttribute('decoding'))
            img.setAttribute('decoding', 'async');
          if (!img.hasAttribute('fetchpriority'))
            img.setAttribute('fetchpriority', 'low');
        });

        frag.appendChild(item);
      }

      targetGrid.appendChild(frag);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
  };

  // ---------- Auto-load gating ----------

  const canAutoLoad = () => {
    const now = performance.now();
    if (now < cooldownUntil) return false;
    if (isLoading) return false;

    // The time and scroll-delta gates exist to space out CONSECUTIVE auto-loads.
    // Before any load has happened they must not apply: the observer fires once
    // when it starts watching, and if the sentinel is already in view on a short
    // page that single callback was rejected and nothing re-armed it until the
    // sentinel left and re-entered the viewport.
    const hasLoadedBefore = lastLoadStartedAt > 0;
    if (!hasLoadedBefore) return true;

    if (now - lastLoadStartedAt < MIN_TIME_BETWEEN_LOADS_MS) return false;

    const scrolledSinceLast = lastKnownScrollY - lastTriggerScrollY;
    if (scrolledSinceLast < SCROLL_DELTA_REQUIRED_PX) return false;

    return true;
  };

  const urlKeyWithoutPage = () => {
    const u = new URL(window.location.href);
    u.searchParams.delete('page');
    const entries = [...u.searchParams.entries()].sort(([a], [b]) =>
      a.localeCompare(b),
    );
    u.search = new URLSearchParams(entries).toString();
    return u.pathname + '?' + u.search;
  };

  // ---------- Load more ----------

  async function loadMore({ reason }) {
    const { container, grid, wrapper, sentinel } = getState();
    if (!container || !grid || !wrapper) return;

    // A filter/sort/Back render is on its way (the URL has already moved on),
    // so this grid is about to be replaced: don't fetch its next page.
    if (batchKey && urlKeyWithoutPage() !== batchKey) return;

    // Treat any load-more trigger as “advance one batch”
    shownLimit += batchSize;
    applyBatchVisibility(grid);
    ensureRevealControls(container);
    syncViewedCount(container);

    // Mark this as a “load” event for auto-load gating (even if we don’t fetch)
    lastLoadStartedAt = performance.now();
    lastTriggerScrollY = lastKnownScrollY;

    // If we already have enough cards loaded to satisfy the new limit, stop here.
    // (No network request; we just revealed the next batch.)
    if (countAllGridItems(grid) >= shownLimit) {
      refreshObserver();
      return;
    }

    // If there is no fetch href (final page / reveal-only button), stop here.
    const btnNow = wrapper.querySelector(SELECTORS.button);
    const href = btnNow?.getAttribute('href');
    if (!(typeof href === 'string' && href.trim() !== '')) {
      refreshObserver();
      return;
    }

    const sectionId = grid.dataset.id;
    const fetchUrl = buildSectionUrl(href, sectionId);

    if (!fetchUrl) {
      if (reason === 'click') window.location.href = href;
      return;
    }

    if (intersectionObserver && sentinel)
      intersectionObserver.unobserve(sentinel);

    isLoading = true;

    const generation = loadGeneration;
    const isCurrent = () => generation === loadGeneration && grid.isConnected;
    const controller = new AbortController();
    loadAbortController = controller;
    const loadTimeout = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);

    setButtonLoading(container, true);
    clearTimeout(spinnerTimer);
    spinnerTimer = setTimeout(
      () => setSpinnerVisible(container, true),
      SPINNER_DELAY_MS,
    );

    try {
      let html = prefetchedPages.get(fetchUrl);
      if (html != null) {
        prefetchedPages.delete(fetchUrl);
      } else {
        const res = await fetch(fetchUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
        });

        if (res.status === 429) {
          cooldownUntil = performance.now() + 2500;
          showCooldownMessage(container, 2500);
          return;
        }

        if (!res.ok) throw new Error(`Load more failed: ${res.status}`);

        html = await res.text();
      }

      const { newGrid, newWrapper } = parseSectionHTML(html);
      if (!newGrid) throw new Error('Could not find next grid in response');

      if (!isCurrent()) return;
      await appendItemsChunked(grid, newGrid, isCurrent);
      if (!isCurrent()) return;

      // Replace pagination wrapper from server (updates next href + may remove button)
      const currentWrapper = container.querySelector(SELECTORS.wrapper);
      if (currentWrapper && newWrapper)
        currentWrapper.innerHTML = newWrapper.innerHTML;

      // Align batching to the current grid columns, and ensure we end on a full row
      normalizeBatching(container);

      // Re-apply batching + ensure we still have a reveal control if needed
      applyBatchVisibility(grid);
      ensureRevealControls(container);
      refreshTotalFromContainer(container);
      syncViewedCount(container);
    } catch (err) {
      // Superseded by a newer grid, which already shows its own state (its
      // reset aborted this request). A timeout takes the error path below.
      if (generation !== loadGeneration) return;
      console.error(err);
      if (reason === 'click') window.location.href = href;
    } finally {
      clearTimeout(loadTimeout);
      if (loadAbortController === controller) loadAbortController = null;
      clearTimeout(spinnerTimer);
      spinnerTimer = null;

      setSpinnerVisible(container, false);
      setButtonLoading(container, false);

      isLoading = false;

      refreshObserver();
      prefetchNextPage(getContainer());
      maybeFoldFinalPage(getContainer());
      // Second chance a beat later, in case the first check ran while the
      // DOM was still settling (idempotent: each href folds at most once).
      window.setTimeout(() => maybeFoldFinalPage(getContainer()), 400);
    }
  }

  // After a load: if only a handful of products remain behind the button, load them now.
  const maybeFoldFinalPage = (container) => {
    if (!container || isLoading) return;
    const minRemaining = readMinRemaining(container);
    if (!minRemaining || totalResults == null) return;

    const grid = container.querySelector(SELECTORS.grid);
    const btn = container.querySelector(SELECTORS.button);
    const href = btn?.getAttribute('href');
    if (!grid || !hasFetchHref(btn)) return;

    const remaining = totalResults - countAllGridItems(grid);
    if (remaining <= 0 || remaining > minRemaining) return;
    if (foldedHrefs.has(href)) return;
    foldedHrefs.add(href);

    loadMore({ reason: 'fold' });
  };

  // ---------- Observer wiring ----------

  const refreshObserver = () => {
    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }

    const { container, button, sentinel } = getState();
    if (!container || !button || !sentinel) return;

    intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || !entry.isIntersecting) return;
        if (!canAutoLoad()) return;

        loadMore({ reason: 'auto' });
      },
      { root: null, rootMargin: OBSERVER_ROOT_MARGIN, threshold: 0 },
    );

    intersectionObserver.observe(sentinel);
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest(SELECTORS.button);
    if (!btn) return;

    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;

    const now = performance.now();
    if (now < cooldownUntil) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    if (isLoading) return;

    loadMore({ reason: 'click' });
  });

  const setupMutationObserver = () => {
    const container = getContainer();
    if (!container) return;

    if (mutationObserver) mutationObserver.disconnect();

    mutationObserver = new MutationObserver(() => {
      const c = getContainer();
      if (!c) return;

      // Reset batching if URL state changed (facets/sort). The page size can change
      // with the filtered count, so re-read it from the freshly rendered grid.
      const keyNow = urlKeyWithoutPage();
      if (batchKey && batchKey !== keyNow) {
        // Retire any load started for the grid that was just replaced.
        loadGeneration += 1;
        loadAbortController?.abort();
        foldedHrefs.clear();
        syncBaseBatchSize(c);
        shownLimit = batchSize;
        batchKey = keyNow;
        prefetchedPages.clear();
      } else if (!batchKey) {
        batchKey = keyNow;
      }

      // Align batching to the current grid columns (and keep a full row before the button)
      normalizeBatching(c);

      const grid = c.querySelector(SELECTORS.grid);
      applyBatchVisibility(grid);
      ensureRevealControls(c);

      refreshTotalFromContainer(c);
      syncViewedCount(c);
      refreshObserver();
      prefetchNextPage(c);
    });

    mutationObserver.observe(container, { childList: true });
  };

  document.addEventListener('DOMContentLoaded', () => {
    const container = getContainer();
    if (!container) return;

    batchKey = urlKeyWithoutPage();
    syncBaseBatchSize(container);
    shownLimit = batchSize;

    // Compute columns and adjust batching so we render a clean, full row above the button
    normalizeBatching(container);

    setSpinnerVisible(container, false);

    const grid = container.querySelector(SELECTORS.grid);
    applyBatchVisibility(grid);
    ensureRevealControls(container);

    refreshTotalFromContainer(container);
    syncViewedCount(container);
    refreshObserver();
    setupMutationObserver();
    prefetchNextPage(container);
  });
})();
